import type { AnyTransactionItem } from '@/utils/tx-list'
import type { ReactElement } from 'react'
import { useContext } from 'react'
import { Typography } from '@safe-global/views/components/ui/typography'
import { isMultisigExecutionInfo } from '@/utils/transaction-guards'
import ExpandableTransactionItem from '@/components/transactions/TxListItem/ExpandableTransactionItem'
import css from './styles.module.css'
import { ReplaceTxHoverContext, ReplaceTxHoverProvider } from './ReplaceTxHoverProvider'
import ExternalLink from '@safe-global/views/components/common/ExternalLink'

import { HelpCenterArticle } from '@safe-global/utils/config/constants'

const Disclaimer = () => (
  <Typography>
    <b>Conflicting transactions</b>. Executing one will automatically replace the others.{' '}
    <ExternalLink
      href={HelpCenterArticle.CONFLICTING_TRANSACTIONS}
      title="Why are transactions with the same nonce conflicting with each other?"
      noIcon
      className="hover:text-muted-foreground"
    >
      Why did this happen?
    </ExternalLink>
  </Typography>
)

const TxGroup = ({ groupedListItems }: { groupedListItems: AnyTransactionItem[] }): ReactElement => {
  const nonce = isMultisigExecutionInfo(groupedListItems[0].transaction.executionInfo)
    ? groupedListItems[0].transaction.executionInfo.nonce
    : undefined

  const { replacedTxIds } = useContext(ReplaceTxHoverContext)

  return (
    <div className={css.container}>
      <Typography style={{ gridArea: 'nonce' }}>{nonce}</Typography>
      <div className={css.disclaimerContainer} style={{ gridArea: 'warning' }}>
        <Disclaimer />
      </div>
      <div className={css.line} style={{ gridArea: 'line' }} />
      <div className={css.txItems} style={{ gridArea: 'items' }}>
        {groupedListItems.map((tx) => (
          <div
            key={tx.transaction.id}
            className={replacedTxIds.includes(tx.transaction.id) ? css.willBeReplaced : undefined}
          >
            <ExpandableTransactionItem item={tx} isConflictGroup />
          </div>
        ))}
      </div>
    </div>
  )
}

const GroupedTxListItems = ({ groupedListItems }: { groupedListItems: AnyTransactionItem[] }): ReactElement | null => {
  if (groupedListItems.length === 0) return null

  return (
    <ReplaceTxHoverProvider groupedListItems={groupedListItems}>
      <TxGroup groupedListItems={groupedListItems} />
    </ReplaceTxHoverProvider>
  )
}

export default GroupedTxListItems
